import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getKaiServiceLabel, resolveKaiServiceFromPath } from './platformKaiRouting';
import { resolveKaiServiceFromMessageAsync } from './platformKaiRouter';

const PlatformKaiContext = createContext(null);

export function PlatformKaiProvider({ children, defaultServiceKey }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('ask-kai');
  const [manualServiceKey, setManualServiceKey] = useState(null);

  const routeServiceKey = useMemo(
    () => defaultServiceKey || resolveKaiServiceFromPath(location.pathname),
    [defaultServiceKey, location.pathname]
  );

  const activeServiceKey = manualServiceKey || routeServiceKey;

  const openKai = useCallback((serviceKey) => {
    if (serviceKey) setManualServiceKey(serviceKey);
    setIsOpen(true);
  }, []);

  const closeKai = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Async because routing now asks a backend which service owns the question,
  // instead of guessing from a keyword list that fell through to
  // DenialManagement. Callers must await it; it resolves to the keyword result
  // if the lookup fails, so it never leaves a message unrouted.
  const routeMessageToSpecialist = useCallback(
    async (message) => {
      const next = await resolveKaiServiceFromMessageAsync(message, activeServiceKey);
      if (next !== activeServiceKey) {
        setManualServiceKey(next);
      }
      return next;
    },
    [activeServiceKey]
  );

  const value = useMemo(
    () => ({
      isOpen,
      activeTab,
      setActiveTab,
      activeServiceKey,
      activeServiceLabel: getKaiServiceLabel(activeServiceKey),
      routeServiceKey,
      openKai,
      closeKai,
      setManualServiceKey,
      routeMessageToSpecialist,
    }),
    [
      isOpen,
      activeTab,
      activeServiceKey,
      routeServiceKey,
      openKai,
      closeKai,
      routeMessageToSpecialist,
    ]
  );

  return <PlatformKaiContext.Provider value={value}>{children}</PlatformKaiContext.Provider>;
}

export function usePlatformKai() {
  const ctx = useContext(PlatformKaiContext);
  if (!ctx) {
    throw new Error('usePlatformKai must be used within PlatformKaiProvider');
  }
  return ctx;
}

/** Safe for header chrome — returns null when PlatformKaiHost is not mounted. */
export function usePlatformKaiOptional() {
  return useContext(PlatformKaiContext);
}
